import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { React, useEffect, useState, useReducer } from "react";
import {
  characterLimitConstant,
  textFormatConstant,
} from "../../utils/constants";
import ReviseProductDescription from "../ReviseProductDescription";
import { productDescriptionReducer } from "../../utils/reducer";

const characterLimitArray = [
  { label: "Short(100 Characters)", value: "100" },
  { label: "Medium (250 Characters)", value: "250" },
  { label: "Long (500 Characters)", value: "500" },
  { label: "Very Long (1000 Characters)", value: "1000" },
];

const platformLabels = {
  target: {
    title: "Product Title",
    description: "Product Description",
    bulletPoints: "Highlights",
  },
  amazon: {
    title: "Title",
    description: "Description",
    bulletPoints: "About This Item",
  },
  walmart: {
    title: "Product Title",
    description: "Product Description",
    bulletPoints: "Key Features",
  },
  default: {
            title: "Title",
            description: "Description",
            bulletPoints: "Bullet Points"
        }
};

const ProductCardWithInput = (props) => {
  const {
    brand_name,
    product_name,
    product_description,
    collection,
    platform,
    text_format,
    character_limit,
  } = props.productDescriptionState;

  const characterLimit = parseInt(character_limit.replace(/[^0-9]/g, ""), 10);
  const predefinedLimit = characterLimitArray.find(
    (limit) => limit.value == characterLimit
  );
  const isCustomLimit = !predefinedLimit;

  const [showEditArea, setShowEditArea] = useState(false);
  const [responseValue, setResponseValue] = useState("");

  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");

  const [state, dispatch] = useReducer(productDescriptionReducer, {
    textFormat: text_format,
    characterLimit:
      platform === "default"
        ? isCustomLimit
          ? "Custom Limit"
          : predefinedLimit
          ? predefinedLimit.label
          : ""
        : "",
    error: false,
    helperText: "",
    customLimit:
      platform === "default" &&
      characterLimit === "Custom Limit" &&
      isCustomLimit
        ? characterLimit
        : "",
    platform: platform,
    brandName: brand_name,
    productName: product_name,
    productDescription: product_description,
    collectionDescription: collection,
    errors: {
      productName: false,
      brandName: false,
      productDescription: false,
    },
  });

  const [bulletPoints, setBulletPoints] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [newResponseValue, setNewResponseValue] = useState(
    (props.sectionData.productDescriptionTranslate &&
      props.sectionData.productDescriptionTranslate) ||
      {}
  );
  const [platformForLabel, setPlatformForLabel] = useState(platform || "");

  useEffect(() => {
    if (platform === "" || platform === "default") {
      setResponseValue(props.text);
      setNewResponseValue({});
    } else {
      if (props.productTranslateText) {
        props.setIsProductDescriptionLoading(true);
        const { title, description, bulletPoints } = props.productTranslateText;
        if (title) {
          setTitle(title);
        }
        if (description) {
          setDescription(description);
        }
        if (bulletPoints?.length > 0) {
          setBulletPoints(bulletPoints);
        }
      } else {
        const parsedValue = JSON.parse(props.text);
        let points = [];

        setNewResponseValue(parsedValue);

        const productTitle =
          parsedValue["Title"] ||
          parsedValue["title"] ||
          parsedValue["product_title"];
        if (productTitle) {
          setTitle(productTitle);
        }

        const productDesc =
          parsedValue["Description"] ||
          parsedValue["description"] ||
          parsedValue["details"]?.description ||
          parsedValue["product_description"];
        if (productDesc) {
          setDescription(productDesc);
        }

        if (
          Object.keys(parsedValue).includes("Bullet Points") ||
          Object.keys(parsedValue).includes("bullet_points") ||
          (parsedValue.details &&
            Object.keys(parsedValue.details).includes("highlights")) ||
          Object.keys(parsedValue).includes("about_this_item") ||
          Object.keys(parsedValue).includes("key_features") ||
          Object.keys(parsedValue).includes("about this item")
        ) {
          points =
            parsedValue["Bullet Points"] ||
            parsedValue["bullet_points"] ||
            parsedValue["about_this_item"] ||
            parsedValue["key_features"] ||
            (parsedValue.details && parsedValue.details["highlights"]) ||
            parsedValue["about this item"] ||
            [];

          if (points?.length > 0) {
            setBulletPoints(points);
          }
        }

        props.setSectionData((previousState) => ({
          ...previousState,
          productDescriptionTranslate: {
            title: productTitle,
            description: productDesc,
            bulletPoints: points,
          },
        }));
      }
    }
    props.setIsProductDescriptionLoading(false);
  }, [props.text, props.productTranslateText]);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: "15%",
      },
    },
  };

  const handleEditChange = (e) => {
    setResponseValue(e.target.value);
  };

  const handleDefaultPlatformSave = () => {
    if (!responseValue.trim()) {
      return;
    }
    props.setSectionData((previousState) => ({
      ...previousState,
      productDescription: responseValue,
    }));
  };
  const handleOtherPlatformSave = () => {
    if (!title.trim() || !description.trim() || bulletPoints.length === 0) {
      return;
    }
    setNewResponseValue({
      title,
      description,
      bulletPoints,
    });
    props.setSectionData((previousState) => ({
      ...previousState,
      productDescriptionTranslate: {
        title,
        description,
        bulletPoints,
      },
    }));
  };

  const handleIconClick = (e) => {
    if (!props.productDescriptionContentLocked) {
      if (showEditArea) {
        if (platform === "default" || platform === "") {
          handleDefaultPlatformSave();
        } else {
          handleOtherPlatformSave();
        }
      }
      setShowEditArea((prev) => !prev);
    }
  };
  

  const handleContentLocked = () => {
    props.setProductDescriptionContentLocked(
      !props.productDescriptionContentLocked
    );
  };

  const inputField = (type, size, number = false) => (
    <TextField
      fullWidth
      color="tertiary"
      focused
      multiline
      value={""}
      defaultValue={props.productDescriptionState[type]}
      onChange={(e) => {
        const value = e.target.value;
        props.setProductDescriptionState((prevState) => ({
          ...prevState,
          [type]: value,
        }));

        if (number) {
          if (
            value === "" ||
            (Number(value) > 0 && Number(value) <= 3000 && /^\d*$/.test(value))
          ) {
            setError(false);
            setHelperText("");
          } else {
            setError(true);
            setHelperText("Please enter a valid number between 0 and 3000.");
          }
        }
      }}
      InputProps={{
        sx: {
          borderRadius: "0.4rem",
          height: size,
          outline: "none",
        },
      }}
      inputProps={{
        sx: {
          overflow: "auto",
        },
        min: 1,
        max: 3000,
        type: number ? "number" : "text",
      }}
      type={number ? "number" : "text"}
      error={number && error}
      helperText={number && helperText}
    />
  );

  // Function  to clean the bullet points when we have any special characters
  const cleanBulletPoint = (bullet) => {
    return bullet.replace(/^[\s-]+/, "").trim();
  };

  const transformObjDataToText = (data) => {
       const labels = platformLabels[platformForLabel] || platformLabels.default;
        let text = `${labels.title}: ${data.title}\n\n${labels.description}: ${data.description}\n\n${labels.bulletPoints}:\n`;
    data.bulletPoints.forEach((item, index) => {
        text += `${index + 1}. ${item}\n`;
    });

    return text;
  };
  return (
    <div className="w-full flex h-max gap-2 ">
      <div className="w-1/2 flex flex-col">
        <div className="flex justify-between items-center gap-2 mt-4">
          <div className="h-full w-[343px] relative rounded-lg ">
            {props.imagePreview && (
              <div className="relative">
                <img
                  src={require("../../assets/Cancel_Icon_Product Description.png")}
                  alt="CancelIcon"
                  className="absolute right-0 p-3 cursor-pointer"
                  onClick={() => props.cancelIcon()}
                />
                <img
                  src={props.imagePreview}
                  alt="preview"
                  className="h-full w-full object-contain rounded-xl"
                />
              </div>
            )}
          </div>
          <div className="flex flex-col items-center justify-between h-full gap-2">
            <div className="w-full flex flex-col gap-1">
              <label
                htmlFor="brandName"
                className="block text-sm font-medium text-gray-700"
              >
                What is the brand name?
              </label>
              <input
                id="brandName"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                value={state.brandName}
                onChange={(e) => {
                  dispatch({
                    type: "UPDATE_FIELD",
                    field: "brandName",
                    value: e.target.value,
                  });
                  props.setProductDescriptionState({
                    brand_name: e.target.value,
                  });
                  if (e.target.value.length >= 500) {
                    dispatch({
                      type: "VALIDATE_INPUT_ERROR",
                      field: "brandName",
                      value: true,
                    });
                  } else {
                    dispatch({
                      type: "VALIDATE_INPUT_ERROR",
                      field: "brandName",
                      value: false,
                    });
                  }
                }}
                type="text"
              />
            </div>
            <div className="w-full flex flex-col gap-1 ">
              <label
                htmlFor="productName"
                className="block text-sm font-medium text-gray-700"
              >
                What is the product name?
              </label>
              <input
                id="productName"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md "
                value={state.productName}
                onChange={(e) => {
                  dispatch({
                    type: "UPDATE_FIELD",
                    field: "productName",
                    value: e.target.value,
                  });
                  props.setProductDescriptionState({
                    product_name: e.target.value,
                  });
                  if (e.target.value.length >= 500) {
                    dispatch({
                      type: "VALIDATE_INPUT_ERROR",
                      field: "productName",
                      value: true,
                    });
                  } else {
                    dispatch({
                      type: "VALIDATE_INPUT_ERROR",
                      field: "productName",
                      value: false,
                    });
                  }
                }}
                type="text"
              />
            </div>
            <div className="w-full flex flex-col gap-1 ">
              <label
                htmlFor="productDescription"
                className="block text-sm font-medium text-gray-700"
              >
                Provide relevant product details here: (Features and Benefits)
              </label>
              <textarea
                id="productDescription"
                className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
                value={state.productDescription}
                onChange={(e) => {
                  props.setProductDescriptionState({
                    product_description: e.target.value,
                  });
                  dispatch({
                    type: "UPDATE_FIELD",
                    field: "productDescription",
                    value: e.target.value,
                  });
                  if (e.target.value.length >= 1000) {
                    dispatch({
                      type: "VALIDATE_INPUT_ERROR",
                      field: "productDescription",
                      value: true,
                    });
                  } else {
                    dispatch({
                      type: "VALIDATE_INPUT_ERROR",
                      field: "productDescription",
                      value: false,
                    });
                  }
                }}
                rows="4"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col h-full w-full mt-2">
          <div className="w-full flex flex-col gap-1 ">
            <label
              htmlFor="collectionDescription"
              className="block text-sm font-medium text-gray-700"
            >
              If this product is part of a collection or product line, describe
              the collection: (optional)
            </label>
            <textarea
              id="collectionDescription"
              className="mt-1 block w-full p-2 border border-gray-300 rounded-md"
              value={state.collectionDescription}
              onChange={(e) => {
                dispatch({
                  type: "UPDATE_FIELD",
                  field: "collectionDescription",
                  value: e.target.value,
                });
                props.setProductDescriptionState({
                  collection: e.target.value,
                });
              }}
              rows="2"
            />
          </div>
          <div className="flex flex-col gap-2 mt-2 w-full">
            <div className="flex justify-between items-center gap-2">
              <div className="w-[60%]">
                <form className="w-full">
                  <label
                    htmlFor="platform"
                    className="block mb-1 text-xs text-[#6c6c6c]"
                  >
                    Select the e-commerce platform this product description will
                    be published on: (optional)
                  </label>
                  <select
                    id="platform"
                    className="block w-full mt-1 border-gray-300 border px-2 py-2 rounded-md"
                    value={state.platform}
                    onChange={(e) => {
                      const newPlatform = e.target.value;
                      if (
                        newPlatform === "walmart" ||
                        newPlatform === "amazon" ||
                        newPlatform === "target"
                      ) {
                        dispatch({
                          type: "UPDATE_FIELD",
                          field: "customLimit",
                          value: "",
                        });
                      }
                      dispatch({
                        type: "UPDATE_FIELD",
                        field: "platform",
                        value: newPlatform,
                      });
                      props.setProductDescriptionState({
                        platform: newPlatform,
                      });
                    }}
                  >
                    <option value="" disabled>
                      Select the Platform
                    </option>
                    {["Default", "Amazon", "Walmart", "Target"].map((value) => (
                      <option key={value} value={value.toLowerCase()}>
                        {value}
                      </option>
                    ))}
                  </select>
                </form>
              </div>
              <div className="w-[30%]  mt-4">
                <form className="w-full">
                  <label
                    htmlFor="textFormat"
                    className="block mb-1 text-xs text-[#6c6c6c]"
                  >
                    Text Format: (optional)
                  </label>
                  <select
                    id="textFormat"
                    className="block w-full border-gray-300 border px-2 py-2 rounded-md"
                    value={state.textFormat}
                    onChange={(e) => {
                      props.setProductDescriptionState({
                        text_format: e.target.value,
                      });
                      dispatch({
                        type: "UPDATE_FIELD",
                        field: "textFormat",
                        value: e.target.value,
                      });
                    }}
                    disabled={state.platform !== "default"}
                  >
                    <option value="" disabled={true}>
                      Select Text Format
                    </option>
                    {state.platform === "default" &&
                      textFormatConstant &&
                      textFormatConstant.map((format) => (
                        <option key={format} value={format}>
                          {format}
                        </option>
                      ))}
                  </select>
                </form>
              </div>
            </div>
            <div className="flex justify-between items-center gap-4">
              <div className="w-[50%]">
                <form className="w-full">
                  <label
                    htmlFor="characterLimit"
                    className="block mb-1 text-xs text-[#6c6c6c]"
                  >
                    Character Limit: (optional)
                  </label>
                  <select
                    id="characterLimit"
                    className="block w-full mt-1 border-gray-300 border px-2 py-2 rounded-md"
                    value={state.characterLimit}
                    onChange={(e) => {
                      const newCharacterLimit = e.target.value;
                      dispatch({
                        type: "UPDATE_FIELD",
                        field: "characterLimit",
                        value: newCharacterLimit,
                      });
                      props.setProductDescriptionState({
                        character_limit: newCharacterLimit,
                      });

                      if (newCharacterLimit !== "Custom Limit") {
                        dispatch({
                          type: "UPDATE_FIELD",
                          field: "customLimit",
                          value: "",
                        });
                      }
                    }}
                    disabled={state.platform !== "default"}
                  >
                    <option value="" disabled={true} selected>
                      Select Character Limit
                    </option>
                    {state.platform === "default" &&
                      characterLimitConstant &&
                      Object.keys(characterLimitConstant).map((key, index) => (
                        <option key={index} value={key}>
                          {characterLimitConstant[key]}
                        </option>
                      ))}
                  </select>
                </form>
              </div>
              <div className="w-1/3">
                <form className="w-full">
                  <label
                    htmlFor="customLimit"
                    className="block mb-1 text-xs text-[#6c6c6c]"
                  >
                    Custom Limit: (optional)
                  </label>
                  <input
                    type="text"
                    id="customLimit"
                    className={`block w-full mt-1 border-gray-300 border px-2 py-2 rounded-md ${
                      state.characterLimit !== "Custom Limit"
                        ? "cursor-not-allowed bg-gray-100"
                        : ""
                    } `}
                    value={state.customLimit}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (
                        value === "" ||
                        (Number(value) > 0 &&
                          Number(value) <= 3000 &&
                          /^\d*$/.test(value))
                      ) {
                        dispatch({ type: "SET_ERROR", value: false });
                        dispatch({ type: "SET_HELPER_TEXT", value: "" });
                      } else {
                        dispatch({ type: "SET_ERROR", value: true });
                        dispatch({
                          type: "SET_HELPER_TEXT",
                          value:
                            "Please enter a valid number between 0 and 3000.",
                        });
                      }

                      if (state.characterLimit === "Custom Limit") {
                        props.setProductDescriptionState({
                          character_limit: e.target.value,
                        });
                        dispatch({
                          type: "UPDATE_FIELD",
                          field: "customLimit",
                          value,
                        });
                      }
                    }}
                    disabled={state.characterLimit !== "Custom Limit"}
                  />
                </form>
              </div>
              <div className="w-1/3 mt-5">
                <Button
                  className="submit-button-productDescription"
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  disabled={
                    state.brandName === "" ||
                    state.productName === "" ||
                    state.productDescription === "" ||
                    state.error ||
                    Object.values(state.errors).some((error) => error) ||
                    (state.platform === "default" &&
                      state.characterLimit === "Custom Limit" &&
                      state.customLimit === "")
                  }
                  onClick={() => {
                    props.GeneratePromtProductDescription();

                    props.handleTranslateLanguage("English", props.activeTab);
                    setPlatformForLabel(state.platform);
                  }}
                  sx={{
                    border: " 1px solid #26890d",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "transparent",
                      borderColor: "currentColor",
                    },
                  }}
                >
                  SUBMIT
                </Button>
              </div>
            </div>
            <div className="flex justify-center items-center w-full">
              {state.error && (
                <p className=" w-full text-center text-sm text-red-500">
                  {state.helperText}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex w-1/2 gap-2">
        <div className="h-full flex items-center mx-2">
          <div className="border h-full border-[#DFDFDF]"></div>
        </div>
        <div className="w-full flex flex-col">
          <div className="flex flex-col  sm:flex-row h-[21rem]  mb-2 border border-gray-300 rounded-md">
            {showEditArea ? (
              <>
                {platform === "default" || platform === "" ? (
                  <TextField
                    variant="standard"
                    sx={{
                      width: "100%",
                      minHeight: "14.375rem",
                      maxHeight: "20.875rem",
                      overflowY: "auto",
                      "& fieldset": { border: "none" },
                      marginTop: "0.625rem",
                    }}
                    fullWidth
                    multiline
                    defaultValue={responseValue}
                    onChange={handleEditChange}
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        color: "#000",
                        fontFamily: "Open Sans, sans-serif",
                        fontSize: "0.875rem",
                        fontStyle: "normal",
                        fontWeight: 400,
                        lineHeight: "1.875rem",
                      },
                    }}
                    inputRef={(input) => input && input.focus()}
                    onFocus={(e) =>
                      e.currentTarget.setSelectionRange(
                        e.currentTarget.value.length,
                        e.currentTarget.value.length
                      )
                    }
                  />
                ) : (
                  <div className="w-full flex flex-col overflow-y-scroll overflow-x-hidden">
                    <TextField
                      variant="standard"
                      sx={{
                        width: "100%",
                        margin: "0.5rem",
                        marginBottom: "1rem",
                      }}
                      fullWidth
                      multiline
                      label="Title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          color: "#000",
                          fontFamily: "Open Sans, sans-serif",
                          fontSize: "0.875rem",
                          fontStyle: "normal",
                          fontWeight: 400,
                          lineHeight: "1.875rem",
                        },
                      }}
                    />
                    <TextField
                      variant="standard"
                      sx={{
                        width: "100%",
                        margin: "0.5rem",
                        marginBottom: "1rem",
                      }}
                      fullWidth
                      multiline
                      label="Description"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          color: "#000",
                          fontFamily: "Open Sans, sans-serif",
                          fontSize: "0.875rem",
                          fontStyle: "normal",
                          fontWeight: 400,
                          lineHeight: "1.875rem",
                        },
                      }}
                    />
                    <TextField
                      variant="standard"
                      sx={{
                        width: "100%",
                        margin: "0.5rem",
                        marginBottom: "1rem",
                      }}
                      fullWidth
                      multiline
                      label="Bullet Points"
                      value={bulletPoints.join("\n")}
                      onChange={(e) =>
                        setBulletPoints(e.target.value.split("\n"))
                      }
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          color: "#000",
                          fontFamily: "Open Sans, sans-serif",
                          fontSize: "0.875rem",
                          fontStyle: "normal",
                          fontWeight: 400,
                          lineHeight: "1.875rem",
                        },
                      }}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="w-full overflow-auto p-2 px-4 whitespace-pre-wrap h-full leading-9 text-black font-sans text-sm font-normal">
                {Object.keys(newResponseValue).length > 0 ? (
                  <>
                    <p>
                      {" "}
                      <span className="font-semibold ">
                        {" "}
                        {platformLabels[platformForLabel]?.title}
                      </span>{" "}
                      : {title && title}{" "}
                    </p>
                    <p>
                      <span className="font-semibold">
                        {platformLabels[platformForLabel]?.description}
                      </span>
                      : {description && description}
                    </p>

                    <span className="font-semibold">
                      {platformLabels[platformForLabel]?.bulletPoints}:
                    </span>
                    {bulletPoints?.length > 0 ? (
                      <>
                        {bulletPoints?.map((bullet) => (
                          <li key={bullet}>{cleanBulletPoint(bullet)}</li>
                        ))}
                      </>
                    ) : null}
                  </>
                ) : (
                  <>{responseValue}</>
                )}
              </div>
            )}

            <div className="sm:pl-4 flex flex-row sm:flex-col justify-around sm:justify-end items-center">
              {!showEditArea ? (
                <div>
                  <Button variant="text" onClick={handleIconClick}>
                    <img
                      src={require("../../assets/edit-icon.png")}
                      className="editiconProductDescription"
                      alt="edit icon"
                    />
                  </Button>
                </div>
              ) : (
                <Button variant="text" onClick={handleIconClick}>
                  <img
                    src={require("../../assets/saveIcon.png")}
                    alt="Save Icon"
                    className="saveIconProductDescription"
                  />
                </Button>
              )}

              <div>
                <Button onClick={handleContentLocked} disabled={showEditArea}>
                  {showEditArea ? (
                    <img
                      src={require(`../../assets/disabledLock.png`)}
                      alt="disabled-lock"
                      className="mb-4 text-gray-500 h-[1.5rem] w-[1.5rem]"
                    />
                  ) : (
                    <img
                      src={require(`../../assets/${
                        props.productDescriptionContentLocked
                          ? "content-lock"
                          : "content-unlock"
                      }.jpg`)}
                      alt="locked-Icon"
                      className="lockIconProductDescription mt-4 sm:mt-0 mb-[1.06rem]"
                    />
                  )}
                </Button>
              </div>

              <div>
                <Tooltip title="Send To Translate Tab">
                  <Button
                    onClick={() => {
                      if (platform === "default" || platform === "") {
                        props.setInputText(props.text);
                      } else {
                        const data = transformObjDataToText(
                          props.productTranslateText
                        );
                        props.setInputText(data);
                      }

                      props.setSelectedLanguage(
                        props.translateLanguage?.[props.activeTab][
                          props.selectedTitle
                        ]
                      );
                    }}
                    disabled={showEditArea}
                  >
                    {showEditArea ? (
                      <img
                        src={require("../../assets/disabledTranslate.png")}
                        className=" mb-[10px] mt-px h-[1.5rem] w-[1.5rem]"
                        alt="disabled-send-to-translate"
                      />
                    ) : (
                      <img
                        src={require("../../assets/send-to-translate.png")}
                        className=" text-[#26890d] mt-3 sm:mt-[0.63rem] mb-[0.625rem]"
                        alt="Send to translate tab"
                      />
                    )}
                  </Button>
                </Tooltip>
              </div>
            </div>
          </div>

          <ReviseProductDescription
            GeneratePromtProductDescription={
              props.GeneratePromtProductDescription
            }
            productDescriptionContentLocked={
              props.productDescriptionContentLocked
            }
            setIsProductDescriptionLoading={
              props.setIsProductDescriptionLoading
            }
            setSectionData={props.setSectionData}
            imagePreview={props.imagePreview}
            sectionData={props.sectionData}
            platform={platformForLabel}
          />
        </div>
      </div>
    </div>
  );
};

export default ProductCardWithInput;
